import React, { useState } from 'react';
import { Property, Unit, Invoice } from '../types';
import { MapPin, ChevronDown, ChevronUp, Plus, User, CheckCircle, AlertCircle, Coins, FileText, Edit2 } from 'lucide-react';

interface PropertyCardProps {
  property: Property;
  invoices: Invoice[];
  onAddTenant: (propertyId: string) => void;
  onAddExpense: (propertyId: string) => void;
  onAddUnit?: (propertyId: string) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property, invoices, onAddTenant, onAddExpense, onAddUnit }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate stats logic
  const totalUnits = property.units.length;
  const occupiedUnits = property.units.filter(u => u.tenantId).length; // In real data, check if linked
  const occupancyRate = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
  
  // Mock financial calculation (in a real app, sum from actual invoices/payments)
  const totalExpected = property.units.reduce((sum, u) => sum + Number(u.rent || 0), 0);
  
  // Fix: Calculate collected amount from actual Paid invoices for this property
  const propertyInvoices = invoices.filter(inv => String(inv.propertyId) === String(property.id));
  const totalCollected = propertyInvoices
    .filter(inv => inv.status === 'Paid' || inv.isPaid)
    .reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
    
  const collectionRate = totalExpected > 0 ? Math.round((totalCollected / totalExpected) * 100) : 0;
  const netRevenue = totalCollected; // Minus expenses in real logic

  return (
    <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden mb-6 transition-all hover:shadow-lg">
      <div className="p-6">
        {/* Header Row */}
        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4 mb-6">
            <div>
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    {property.name}
                    <span className={`px-2 py-0.5 rounded text-xs ${property.status === 'نشط' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {property.status}
                    </span>
                </h3>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {property.governorate}, {property.city}
                </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
                <button onClick={() => onAddTenant(property.id)} className="px-4 py-2 bg-fly-50 text-fly-700 rounded-lg text-sm font-bold hover:bg-fly-100 transition-colors flex items-center gap-1">
                    <Plus className="w-4 h-4" /> أضف مستأجر
                </button>
                {onAddUnit && (
                  <button onClick={() => onAddUnit(property.id)} className="px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-bold hover:bg-green-100 transition-colors flex items-center gap-1">
                      <Edit2 className="w-4 h-4" /> تعديل العقار
                  </button>
                )}
                <button onClick={() => onAddExpense(property.id)} className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-bold hover:bg-red-100 transition-colors flex items-center gap-1">
                    <Plus className="w-4 h-4" /> أضف مصروف
                </button>
                <button className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-bold hover:bg-slate-100 transition-colors flex items-center gap-1">
                     <FileText className="w-4 h-4" /> تقرير العمليات
                </button>
            </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="text-sm text-slate-500 mb-1">صافي الإيرادات</div>
                <div className="text-2xl font-bold text-slate-800">{netRevenue.toLocaleString()} KWD</div>
                <div className="w-full bg-slate-200 h-1.5 rounded-full mt-3 overflow-hidden">
                    <div className="bg-green-500 h-full rounded-full" style={{ width: '65%' }}></div>
                </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                    <div className="text-sm text-slate-500">نسبة الإشغال</div>
                    <div className="text-sm font-bold text-blue-600">{occupancyRate}%</div>
                </div>
                <div className="text-lg font-bold text-slate-800 mb-1">
                    {occupiedUnits} <span className="text-sm font-normal text-slate-500">مشغولة من أصل {totalUnits}</span>
                </div>
                <div className="w-full bg-slate-200 h-1.5 rounded-full mt-2 overflow-hidden">
                    <div className="bg-blue-500 h-full rounded-full" style={{ width: `${occupancyRate}%` }}></div>
                </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex justify-between items-center mb-1">
                    <div className="text-sm text-slate-500">نسبة التحصيل</div>
                    <div className="text-sm font-bold text-purple-600">{collectionRate}%</div>
                </div>
                 <div className="text-sm font-bold text-slate-800 mb-1 truncate">
                    {totalCollected.toLocaleString()} <span className="font-normal text-slate-500">مدفوعة من {totalExpected.toLocaleString()}</span>
                </div>
                <div className="w-full bg-slate-200 h-1.5 rounded-full mt-2 overflow-hidden">
                    <div className="bg-purple-500 h-full rounded-full" style={{ width: `${collectionRate}%` }}></div>
                </div>
            </div>
        </div>

        {/* Expand Button */}
        <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full py-3 flex items-center justify-center gap-2 text-sm font-bold text-slate-500 hover:text-fly-600 hover:bg-slate-50 rounded-xl transition-all border-t border-slate-100"
        >
            {isExpanded ? (
                <>إخفاء الوحدات <ChevronUp className="w-4 h-4" /></>
            ) : (
                <>أظهر الوحدات <ChevronDown className="w-4 h-4" /></>
            )}
        </button>
      </div>

      {/* Expanded Units Table */}
      {isExpanded && (
        <div className="bg-slate-50 border-t border-slate-200 p-4 animate-fadeIn">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-right text-sm">
                    <thead className="bg-slate-100 text-slate-600 font-bold">
                        <tr>
                            <th className="p-4 border-b">الوحدة</th>
                            <th className="p-4 border-b">النوع</th>
                            <th className="p-4 border-b">المستأجر</th>
                            <th className="p-4 border-b">الحالة</th>
                            <th className="p-4 border-b">الإيجار</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {property.units.map((unit) => (
                            <tr key={unit.id} className="hover:bg-slate-50">
                                <td className="p-4 font-bold text-slate-800">{unit.unitNumber}</td>
                                <td className="p-4 text-slate-600">{unit.type}</td>
                                <td className="p-4">
                                    {unit.tenantName ? (
                                        <div className="flex items-center gap-2">
                                            <div className="bg-blue-100 p-1 rounded-full"><User className="w-3 h-3 text-blue-600" /></div>
                                            <span className="font-medium text-slate-800">{unit.tenantName}</span>
                                        </div>
                                    ) : (
                                        <span className="text-slate-400 italic">لا يوجد مستأجر</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    {unit.tenantName ? (
                                        (() => {
                                            // Fix: match the YYYY-MM format from server/inputs
                                            const currentMonth = new Date().toISOString().substring(0, 7);
                                            const unitInvoice = invoices.find(inv => 
                                                String(inv.unitId) === String(unit.id) && 
                                                // Handle potential legacy format or just YYYY-MM
                                                (inv.monthDue === currentMonth)
                                            );

                                            if (!unitInvoice) return <span className="inline-flex items-center text-slate-400 bg-slate-50 px-2.5 py-1 rounded-lg text-xs font-bold border border-slate-200">لا توجد فاتورة</span>;
                                            
                                            return unitInvoice.status === 'Paid' ? 
                                                <span className="inline-flex items-center gap-1.5 text-green-700 bg-green-50 px-2.5 py-1 rounded-lg text-xs font-bold border border-green-200 shadow-sm">
                                                    <CheckCircle className="w-3.5 h-3.5"/> 
                                                    <span>مدفوعة</span>
                                                    <span className="text-[10px] opacity-80 border-r border-green-200 pr-1.5 mr-1">{Number(unitInvoice.amount).toLocaleString()} د.ك</span>
                                                </span> : 
                                                <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 px-2.5 py-1 rounded-lg text-xs font-bold border border-red-200 shadow-sm animate-pulse">
                                                    <AlertCircle className="w-3.5 h-3.5"/> 
                                                    <span>مستحقة</span>
                                                    <span className="text-[10px] opacity-80 border-r border-red-200 pr-1.5 mr-1">{Number(unitInvoice.amount).toLocaleString()} د.ك</span>
                                                </span>;
                                        })()
                                    ) : (
                                        <span className="inline-flex items-center text-slate-400 bg-slate-50 px-2 py-1 rounded text-xs">متاحة للتأجير</span>
                                    )}
                                </td>
                                <td className="p-4 font-bold text-slate-800">
                                    {Number(unit.contractRent || unit.rent || 0).toFixed(3)} <span className="text-[10px] text-slate-400 font-normal">KWD</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default PropertyCard;